import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Lock, Mail } from "lucide-react";
import logo from "../assets/logo.png";
import axios from "axios";

// ✅ Use environment variable for API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);

  // ==========================
  // AUTH CHECK ON PAGE LOAD
  // ==========================
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/admin-login");
        return;
      }

      try {
        const res = await axios.get(`${API_BASE_URL}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdmin(res.data.admin || null);
        setLoading(false);
      } catch (error) {
        console.warn("Authentication failed:", error?.response?.data || error);
        localStorage.removeItem("token");
        navigate("/admin-login");
      }
    };

    checkAuth();
  }, [navigate]);

  // ==========================
  // DASHBOARD MENU STRUCTURE
  // ==========================
  const menuItems = [
    {
      name: "Dashboard Overview",
      icon: <LayoutDashboard className="w-7 h-7 sm:w-8 sm:h-8" />,
      path: "/dashboard/home",
      desc: "Manage website homepage content and hero sections",
    },
    {
      name: "User Administration",
      icon: <Users className="w-7 h-7 sm:w-8 sm:h-8" />,
      path: "/dashboard/user-admin",
      desc: "View, edit, or deactivate admin users",
    },
    {
      name: "Change Password",
      icon: <Lock className="w-7 h-7 sm:w-8 sm:h-8" />,
      path: "/dashboard/change-password",
      desc: "Reset or update admin credentials",
    },
    {
      name: "Contact Messages",
      icon: <Mail className="w-7 h-7 sm:w-8 sm:h-8" />,
      path: "/dashboard/contact-queries",
      desc: "Review messages submitted via contact form",
    },
  ];

  // ==========================
  // LOGOUT HANDLER
  // ==========================
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/admin-login");
  };

  // ==========================
  // LOADING STATE
  // ==========================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-700 text-lg font-medium px-4 text-center">
        Checking access...
      </div>
    );
  }

  // ==========================
  // RENDER DASHBOARD
  // ==========================
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 to-blue-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between sm:justify-start gap-3 w-full">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={logo}
                alt="HPR Infra Logo"
                className="h-10 sm:h-12 md:h-14 w-auto object-contain shrink-0"
              />
              {admin?.name && (
                <span className="text-xs sm:text-sm text-gray-600 truncate">
                  Logged in as <span className="font-semibold">{admin.name}</span>
                </span>
              )}
            </div>

            <button
              onClick={handleLogout}
              className="sm:hidden px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold shadow hover:from-green-600 hover:to-green-700 transition-all text-sm shrink-0"
            >
              Logout
            </button>
          </div>

          <button
            onClick={handleLogout}
            className="hidden sm:inline-flex px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold shadow hover:from-green-600 hover:to-green-700 transition-all"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-3 sm:px-6 py-6 sm:py-10">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-10 lg:p-14 w-full max-w-5xl">
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-800 text-center mb-6 sm:mb-14">
            Admin Control Panel
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 lg:gap-10">
            {menuItems.map((item, index) => (
              <div
                key={index}
                onClick={() => navigate(item.path)}
                className="group bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl sm:rounded-3xl shadow-md hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-300 cursor-pointer p-5 sm:p-8 lg:p-10 flex flex-col items-center text-center"
              >
                <div className="bg-gradient-to-br from-green-400 to-blue-400 text-white p-4 sm:p-5 rounded-full mb-4 sm:mb-6 group-hover:scale-105 sm:group-hover:scale-110 transition-transform duration-200">
                  {item.icon}
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 group-hover:text-green-600 transition-colors mb-2">
                  {item.name}
                </h3>

                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
